import { useMemo, useCallback, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import type { SnackbarAction } from 'notistack';

import { getDocumentName } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';
import { PATH_SECTION_TEMPLATES_NEW_PRINTER } from '@e-socna/printer-contract';

import { Breadcrumbs } from '../../components/Breadcrumbs';
import { DocumentEditor } from '../../components/DocumentEditor';
import { NotiStackButton } from '../../components/NotiStackButton';
import { Loading } from '../../components/Loading';
import { userFriendlyErrors } from '../../utils/back-errors.util';
import {
  PATH_DOCUMENT_AND_SECTION_TEMPLATES_DEFAULT,
  PATH_SECTION_TEMPLATE_DEFAULT,
  PATH_SECTION_TEMPLATES,
  PATH_SECTION_TEMPLATES_NEW_DEFAULT,
  PATH_SECTION_TEMPLATES_NEW_INFO,
  PATH_SECTION_TEMPLATES_NEW_INFO_EDIT,
  PATH_SECTION_TEMPLATES_NEW_EDITOR,
  PATH_SECTION_TEMPLATES_NEW_SUMMARY,
} from '../../app/constants/paths.constant';
import { FormVariant } from '../../forms';
import { memoForm2ApiMapper } from '../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { BackupMode } from '../../components/DocumentEditor/enums/backup-mode.enum';
import { Page } from '../../contexts/NavigationContext/enums/page.enum';
import { useNavigationContext } from '../../contexts/NavigationContext/hooks/useNavigationContext.hook';
import { useViewSnackbarAction } from '../../hooks/useViewSnackbarAction.hook';
import type { BackError } from '../../utils/back-errors.util';
import type { MemosState } from '../../forms/MemoBeforeSubmit';

import { SectionTemplatesPage_List_AllSectionTemplates_QueryDocument } from '../DocumentAndSectionTemplates/hooks/useFindAllSectionTemplatesQuery.hook';
import { useGetters } from '../SectionTemplate/hooks/useGetters.hook';
import { SectionTemplateInfoTabPanel } from '../SectionTemplate/SectionTemplateInfo';
import type { SectionTemplatePage_OneSectionTemplate } from '../SectionTemplate/hooks/useFindOneSectionTemplateQuery.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';
import { MessageList } from '../../components/MessageList';

import { sectionTemplateForm2ApiMapper } from './mappers/section-template.form2api.mapper';
import { useCreateSectionTemplateMutation } from './hooks/useCreateSectionTemplateMutation.hook';
import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';

interface Props {
  readonly isInPrintMode?: boolean;
}

export const NewSectionTemplatePage = ({
  isInPrintMode = false,
}: Readonly<Props>) => {
  const { setPage } = useNavigationContext();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { buildViewSnackbarAction } = useViewSnackbarAction();
  const [isCreating, setIsCreating] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: createSectionTemplateMutation } =
    useCreateSectionTemplateMutation();

  useEffect(() => {
    setPage(Page.DocumentAndSectionTemplates);
  }, [setPage]);

  const editorModePaths = useMemo(
    () => ({
      Info: PATH_SECTION_TEMPLATES_NEW_INFO,
      InfoEdit: PATH_SECTION_TEMPLATES_NEW_INFO_EDIT,
      Editor: PATH_SECTION_TEMPLATES_NEW_EDITOR,
      Printer: PATH_SECTION_TEMPLATES_NEW_PRINTER,
      Summary: PATH_SECTION_TEMPLATES_NEW_SUMMARY,
    }),
    [],
  );

  const onSave = useCallback(
    (
      values: SectionTemplatePage_OneSectionTemplate,
      memos: MemosState,
      handleSuccess?: () => void,
      handleError?: () => void,
      // eslint-disable-next-line max-params
    ) => {
      setIsCreating(true);

      const onSuccess = () => {
        setIsCreating(false);

        enqueueSnackbar('Votre nouveau modèle de section a bien été créé.', {
          action: buildViewSnackbarAction(
            buildPath(PATH_SECTION_TEMPLATE_DEFAULT, { id: values._id }),
          ),
        });

        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(
              SectionTemplatesPage_List_AllSectionTemplates_QueryDocument,
            ),
          ],
        });

        handleSuccess?.();

        // DIRTY : Wait a little for the editor to update its latestSavedVersion before navigating
        // in order to avoid unnecessary prompt display
        setTimeout(() => {
          void navigate(PATH_SECTION_TEMPLATES);
        }, 50);
      };

      const onError = (error: unknown) => {
        setIsCreating(false);
        const backError = error as BackError;
        let action: SnackbarAction | null = null;

        const supportedErrors = [
          'NO_CODE_OR_LABEL',
          'CODE_ALREADY_USED',
          'EMPTY_SECTION',
        ];

        const supportedErrorCode = backError.response.errors.find(
          (error) =>
            error.extensions.errCode &&
            supportedErrors.includes(error.extensions.errCode),
        )?.extensions.errCode;

        if (supportedErrorCode) {
          let to: (typeof editorModePaths)[keyof typeof editorModePaths] =
            editorModePaths.Editor;

          switch (supportedErrorCode) {
            case 'NO_CODE_OR_LABEL':
              to = editorModePaths.InfoEdit;
              break;
            case 'CODE_ALREADY_USED':
              to = editorModePaths.InfoEdit;
              break;
            case 'EMPTY_SECTION':
              to = editorModePaths.Editor;
              break;
            default:
              console.warn(
                `Unknown error code in NewSectionTemplatePage onSave: ${supportedErrorCode}`,
              );
          }

          action = (snackbarId) => (
            <NotiStackButton
              // eslint-disable-next-line react/jsx-no-bind
              onClick={() => {
                void navigate(to);
                closeSnackbar(snackbarId);
              }}
              variant="text"
            >
              Modifier
            </NotiStackButton>
          );
        }

        enqueueSnackbar(
          <MessageList messages={userFriendlyErrors(error as BackError)} />,
          {
            variant: 'error',
            action,
          },
        );

        handleError?.();
      };

      createSectionTemplateMutation(
        {
          sectionTemplate: sectionTemplateForm2ApiMapper(values),
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError,
          onSuccess,
        },
      );
    },
    [
      buildViewSnackbarAction,
      createSectionTemplateMutation,
      enqueueSnackbar,
      navigate,
      queryClient,
      editorModePaths.Editor,
      editorModePaths.InfoEdit,
      closeSnackbar,
    ],
  );

  const { initialValues } = useInitialValuesFactory();

  const getters = useGetters();

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!isCreating && <Loading />}
      <Helmet>
        <title>{buildDocumentTitle("Création d'un Modèle de Section")}</title>
      </Helmet>
      {!isInPrintMode && (
        <>
          <Breadcrumbs
            paths={[
              {
                to: PATH_DOCUMENT_AND_SECTION_TEMPLATES_DEFAULT,
                label: 'modèles',
                key: 'documentAndSectionTemplates',
              },
              {
                to: PATH_SECTION_TEMPLATES,
                label: 'sections',
                key: 'sectionTemplates',
              },
              {
                to: PATH_SECTION_TEMPLATES_NEW_DEFAULT,
                label: 'nouveau',
                key: 'newSectionTemplate',
              },
            ]}
          />
          <PageTitle title="Création d'un Modèle de Section" />
        </>
      )}
      <DocumentEditor
        componentSettings={{
          InfoTabPanel: SectionTemplateInfoTabPanel,
          welcomeMessage:
            'Votre modèle de section est vide pour le moment. Commencez le travail !',
        }}
        formSettings={{
          formVariant: FormVariant.Creation,
          initialEditable: initialValues,
          isLoading: isCreating,
        }}
        getters={getters}
        handlers={{
          onSave,
        }}
        resolversSettings={{
          allowedReturnTypeIds: undefined,
          enableDirectGeneration: false,
          mustBeResolvableOnBack: false,
        }}
        routerSettings={{
          editorModePaths,
          isInPrintMode,
        }}
        saveSettings={{
          askMemo: {
            beforePrint: false,
            beforeSave: true,
          },
          backupMode: BackupMode.Manual,
        }}
      />
    </>
  );
};
